import { motion } from 'framer-motion';
import { 
  Compass, Eye, Map, Globe, Gamepad2, FlaskConical,
  Waves, Hourglass, PenLine, PersonStanding, Wind,
  RotateCcw, Star, Sparkles, Zap,
  Moon, MoonStar, Lock, MessageCircle, Clock, Languages,
  Palette, Frame, Telescope, BookOpen, Mail, Cake,
  Flower2, Sun, Leaf, Snowflake,
  Trophy, Shield, Flame, Heart, Diamond, Crown
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Map achievement IDs to icons and color schemes
const badgeConfig: Record<string, { icon: LucideIcon; color: string; bg: string }> = {
  // Exploration
  'first-step': { icon: Compass, color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/20' },
  'explorer': { icon: Eye, color: 'text-blue-300', bg: 'bg-blue-300/10 border-blue-300/20' },
  'wanderer': { icon: Map, color: 'text-indigo-400', bg: 'bg-indigo-400/10 border-indigo-400/20' },
  'cartographer': { icon: Globe, color: 'text-purple-400', bg: 'bg-purple-400/10 border-purple-400/20' },
  'completionist-games': { icon: Gamepad2, color: 'text-orange-400', bg: 'bg-orange-400/10 border-orange-400/20' },
  'completionist-experimental': { icon: FlaskConical, color: 'text-pink-400', bg: 'bg-pink-400/10 border-pink-400/20' },
  // Depth
  'deep-thinker': { icon: Waves, color: 'text-cyan-400', bg: 'bg-cyan-400/10 border-cyan-400/20' },
  'patient-soul': { icon: Hourglass, color: 'text-amber-400', bg: 'bg-amber-400/10 border-amber-400/20' },
  'honest-one': { icon: PenLine, color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20' },
  'the-patient': { icon: PersonStanding, color: 'text-teal-400', bg: 'bg-teal-400/10 border-teal-400/20' },
  'breath-master': { icon: Wind, color: 'text-sky-400', bg: 'bg-sky-400/10 border-sky-400/20' },
  // Consistency
  'returner': { icon: RotateCcw, color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/20' },
  'devoted': { icon: Star, color: 'text-yellow-400', bg: 'bg-yellow-400/10 border-yellow-400/20' },
  'steadfast': { icon: Sparkles, color: 'text-amber-300', bg: 'bg-amber-300/10 border-amber-300/20' },
  'eternal': { icon: Zap, color: 'text-yellow-300', bg: 'bg-yellow-300/10 border-yellow-300/20' },
  // Discovery
  'night-owl': { icon: Moon, color: 'text-indigo-300', bg: 'bg-indigo-300/10 border-indigo-300/20' },
  'full-moon': { icon: MoonStar, color: 'text-slate-300', bg: 'bg-slate-300/10 border-slate-300/20' },
  'secret-finder': { icon: Lock, color: 'text-violet-400', bg: 'bg-violet-400/10 border-violet-400/20' },
  'ai-conversant': { icon: MessageCircle, color: 'text-cyan-300', bg: 'bg-cyan-300/10 border-cyan-300/20' },
  'time-traveler': { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-400/10 border-amber-400/20' },
  'polyglot': { icon: Languages, color: 'text-emerald-300', bg: 'bg-emerald-300/10 border-emerald-300/20' },
  // Special
  'dream-weaver': { icon: Palette, color: 'text-purple-300', bg: 'bg-purple-300/10 border-purple-300/20' },
  'self-portrait': { icon: Frame, color: 'text-rose-400', bg: 'bg-rose-400/10 border-rose-400/20' },
  'future-seer': { icon: Telescope, color: 'text-teal-300', bg: 'bg-teal-300/10 border-teal-300/20' },
  'reflector': { icon: BookOpen, color: 'text-blue-300', bg: 'bg-blue-300/10 border-blue-300/20' },
  'letter-writer': { icon: Mail, color: 'text-rose-300', bg: 'bg-rose-300/10 border-rose-300/20' },
  'anniversary': { icon: Cake, color: 'text-pink-300', bg: 'bg-pink-300/10 border-pink-300/20' },
  // Seasonal
  'spring-visitor': { icon: Flower2, color: 'text-pink-400', bg: 'bg-pink-400/10 border-pink-400/20' },
  'summer-visitor': { icon: Sun, color: 'text-yellow-400', bg: 'bg-yellow-400/10 border-yellow-400/20' },
  'autumn-visitor': { icon: Leaf, color: 'text-orange-400', bg: 'bg-orange-400/10 border-orange-400/20' },
  'winter-visitor': { icon: Snowflake, color: 'text-sky-300', bg: 'bg-sky-300/10 border-sky-300/20' },
  // v4.0 New
  'hundred-rooms': { icon: Crown, color: 'text-amber-300', bg: 'bg-amber-300/10 border-amber-300/20' },
  'chain-completer': { icon: Diamond, color: 'text-violet-300', bg: 'bg-violet-300/10 border-violet-300/20' },
  'dreamer': { icon: Moon, color: 'text-indigo-400', bg: 'bg-indigo-400/10 border-indigo-400/20' },
  'fearless': { icon: Flame, color: 'text-red-400', bg: 'bg-red-400/10 border-red-400/20' },
  'creative-spirit': { icon: Palette, color: 'text-amber-300', bg: 'bg-amber-300/10 border-amber-300/20' },
  'future-thinker': { icon: Telescope, color: 'text-teal-400', bg: 'bg-teal-400/10 border-teal-400/20' },
  'playlist-finisher': { icon: Trophy, color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20' },
  'social-soul': { icon: Heart, color: 'text-rose-400', bg: 'bg-rose-400/10 border-rose-400/20' },
  'resilient': { icon: Shield, color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/20' },
};

const defaultBadge = { icon: Star, color: 'text-muted-foreground/40', bg: 'bg-muted/20 border-border/20' };

interface AchievementBadgeProps {
  achievementId: string;
  unlocked: boolean;
  size?: 'sm' | 'md' | 'lg';
  animate?: boolean;
}

const sizeMap = {
  sm: { container: 'w-8 h-8', icon: 'w-3.5 h-3.5' },
  md: { container: 'w-12 h-12', icon: 'w-5 h-5' },
  lg: { container: 'w-16 h-16', icon: 'w-7 h-7' },
};

const AchievementBadge = ({ achievementId, unlocked, size = 'md', animate = false }: AchievementBadgeProps) => {
  const config = badgeConfig[achievementId] || defaultBadge;
  const Icon = config.icon;
  const s = sizeMap[size];

  const badge = (
    <div
      className={`${s.container} rounded-full border flex items-center justify-center transition-all duration-500 ${
        unlocked
          ? `${config.bg} ${config.color}`
          : 'bg-muted/10 border-border/10 text-muted-foreground/15'
      }`}
    >
      <Icon className={`${s.icon} ${unlocked ? '' : 'opacity-30'}`} />
    </div>
  );

  if (animate && unlocked) {
    return (
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
      >
        {badge}
      </motion.div>
    );
  }

  return badge;
};

export { badgeConfig };
export default AchievementBadge;
